import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { getExhibitedArtists } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';

const ArtistsPage = () => {
  const [filter, setFilter] = useState('All');
  const { language } = useLanguage();
  const ro = language === 'ro';
  const artists = useMemo(() => getExhibitedArtists(language), [language]);
  const featured = artists[0];
  const filtered = useMemo(() => {
    if (filter === 'All') return artists;
    return artists;
  }, [artists, filter]);

  return (
    <div className="bg-bone text-charcoal min-h-screen" data-testid="artists-page">
      <Header />

      <main>
        <section className="pt-36 md:pt-44 lg:pt-52 pb-14 md:pb-20 px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            <Reveal delay={0.1} className="col-span-12">
              <nav className="text-[11px] tracking-eyebrow uppercase text-charcoal/50 mb-6 flex items-center gap-2">
                <Link to="/" className="hover:opacity-60 transition-opacity">Home</Link>
                <span>/</span>
                <span className="text-charcoal/80">Artists</span>
              </nav>
              <h1
                className="font-sans font-extrabold text-charcoal leading-[0.9] tracking-normal text-6xl sm:text-7xl md:text-[9vw] lg:text-[8vw]"
                data-testid="artists-page-title"
              >
                {ro ? 'Artiști' : 'Artists'}
              </h1>
              <p className="mt-8 md:mt-10 max-w-3xl text-charcoal/75 text-base md:text-lg leading-relaxed font-bold">
                {ro ? 'Artiști care au făcut parte din programul expozițional Dimir and Son Gallery.' : 'Artists who have been part of the Dimir and Son Gallery exhibition programme.'}
              </p>
            </Reveal>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <Reveal>
            <div className="grid grid-cols-12 gap-x-6 gap-y-8 border-t border-charcoal pt-8 md:pt-10">
              <div className="col-span-12 lg:col-span-7">
                <div className="relative w-full aspect-[16/10] overflow-hidden bg-beige">
                  <motion.img
                    src={featured.image}
                    alt={featured.name}
                    initial={{ scale: 1.06 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 w-full h-full object-contain"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="col-span-12 lg:col-span-5 lg:self-end">
                <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                  {ro ? 'Artist expus' : 'Exhibited artist'}
                </span>
                <h2 className="mt-5 font-sans font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-normal">
                  {featured.name}
                </h2>
                <div className="mt-5 text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                  {featured.discipline} - {featured.city}
                </div>
                <p className="mt-7 text-base md:text-lg leading-relaxed text-charcoal/75 font-bold whitespace-pre-line">
                  {featured.statement}
                </p>
                <Link to={`/artists/${featured.id}`} className="editorial-link mt-8">
                  <span>{ro ? 'Vezi profilul' : 'View profile'}</span>
                  <span className="arrow">→</span>
                </Link>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-36 border-t border-charcoal pt-16 md:pt-24">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
              <h2 className="font-sans font-extrabold text-4xl md:text-5xl leading-none">
                {ro ? 'Artiști expuși' : 'Exhibited artists'}
              </h2>
              <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/50">{artists.length} {ro ? 'artiști' : 'artists'}</span>
            </div>
          </Reveal>

          <ul className="border-t border-charcoal">
            {filtered.map((artist, index) => (
              <ArtistRow key={artist.id} artist={artist} index={index} ro={ro} />
            ))}
          </ul>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const ArtistRow = ({ artist, index, ro }) => {
  const artistWorks = [];

  return (
    <motion.li
      id={artist.id}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: (index % 4) * 0.05 }}
      className="group border-b border-charcoal py-8 md:py-10"
      data-testid={`artist-row-${artist.id}`}
    >
      <div className="grid grid-cols-12 gap-x-6 gap-y-6 items-start">
        <div className="col-span-2 md:col-span-1">
          <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/50">
            {artist.number}
          </span>
        </div>

        <div className="col-span-10 md:col-span-4">
          <h3 className="font-sans font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-normal text-charcoal">
            {artist.name}
          </h3>
          <p className="mt-3 text-[11px] tracking-eyebrow uppercase text-charcoal/60">
            {artist.city}
          </p>
        </div>

        <div className="col-span-12 md:col-span-3">
          <p className="text-[11px] tracking-eyebrow uppercase text-charcoal/55">
            {artist.discipline}
          </p>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/75 font-bold whitespace-pre-line">
            {artist.statement}
          </p>
        </div>

        <div className="col-span-7 md:col-span-2 text-[11px] tracking-eyebrow uppercase text-charcoal/60">
          <div>{artist.status}</div>
          {artistWorks.length > 0 && (
            <div className="mt-3 space-y-1 text-charcoal/45">
              {artistWorks.map((work) => (
                <div key={work.title}>{work.title}</div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-5 md:col-span-2 justify-self-end">
          <Link to={`/artists/${artist.id}`} className="editorial-link">
            <span className="hidden sm:inline">{ro ? 'Profil' : 'Profile'}</span>
            <span className="arrow">→</span>
          </Link>
        </div>
      </div>
    </motion.li>
  );
};

export default ArtistsPage;
